/* verqo — Homepage SAMPLE rebuilt against /app/design_guidelines.json · June 2026
   Enterprise-fintech discipline: Inter, left-aligned type, restrained lime,
   credibility furniture (metrics, partner wall, compliance bar, dense footer).
   SAMPLE ONLY at /preview/home-corp — "/" and /preview/home-punch are untouched. */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Banknote, ShieldCheck, Truck, Wallet, Store,
  Radar, Layers, Building2, FileText, Lock, BadgeCheck, Landmark,
} from "lucide-react";
import { PreviewNavHeader } from "../../components/marketing/PreviewNavHeader";

const INK = "#0b1914";
const PAPER = "#f7f6f2";
const LIME = "#dcff3a";

const Rise = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Label = ({ children, dark = false, className = "" }) => (
  <div className={`text-[11px] uppercase tracking-[0.16em] font-semibold ${dark ? "text-white/40" : "text-slate-500"} ${className}`}>
    {children}
  </div>
);

/* product UI framed the way Stripe/Ramp do it: 1px border, layered diffuse shadow, tight crop */
function UIFrame({ src, url, alt, crop = 1, className = "", dark = false }) {
  return (
    <div className={`relative rounded-xl overflow-hidden bg-white ${className}`}
         style={{
           border: dark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(11,25,20,0.08)",
           boxShadow: dark
             ? "0 2px 6px rgba(0,0,0,0.30), 0 24px 60px -20px rgba(0,0,0,0.55)"
             : "0 1px 2px rgba(11,25,20,0.04), 0 18px 44px -18px rgba(11,25,20,0.16)",
         }}>
      <div className="flex items-center gap-1.5 px-3 h-8 border-b" style={{ borderColor: "rgba(11,25,20,0.07)", background: "#fbfbfa" }}>
        <span className="w-2 h-2 rounded-full" style={{ background: "rgba(11,25,20,0.14)" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "rgba(11,25,20,0.14)" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "rgba(11,25,20,0.14)" }} />
        {url && <div className="ml-2.5 text-[10px] font-mono text-slate-400 truncate">{url}</div>}
      </div>
      <div className="overflow-hidden">
        <img src={src} alt={alt} loading="lazy" className="block w-full h-auto"
             style={{ transform: `scale(${crop})`, transformOrigin: "top left", width: `${100 / crop}%` }} />
      </div>
    </div>
  );
}

/* callout that looks like a real app component, not a marketing sticker */
function Callout({ label, value, meta, className, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute hidden xl:block rounded-lg bg-white px-3.5 py-2.5 ${className}`}
      style={{ border: "1px solid rgba(11,25,20,0.08)", boxShadow: "0 1px 2px rgba(0,0,0,0.05), 0 16px 36px -14px rgba(0,0,0,0.35)", minWidth: 168 }}
    >
      <div className="text-[9.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">{label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-[17px] font-semibold tracking-tight text-slate-900">{value}</span>
        {meta && <span className="text-[10.5px] text-slate-400">{meta}</span>}
      </div>
    </motion.div>
  );
}

const PARTNERS = [
  { d: "allianz-trade.com", n: "Allianz Trade" },
  { d: "shopify.com", n: "Shopify" },
  { d: "quickbooks.intuit.com", n: "QuickBooks" },
  { d: "netsuite.com", n: "NetSuite" },
  { d: "ups.com", n: "UPS" },
  { d: "fedex.com", n: "FedEx" },
  { d: "dhl.com", n: "DHL" },
  { d: "plaid.com", n: "Plaid" },
];

const FLOW = [
  { i: Layers, t: "Order" }, { i: Truck, t: "Shipment" }, { i: ShieldCheck, t: "Insurance" },
  { i: Banknote, t: "Financing" }, { i: Wallet, t: "Payment" },
];

export default function HomeCorpPreview() {
  useEffect(() => {
    const id = "verqo-inter-font";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id; link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,400;14..32,500;14..32,600;14..32,700&display=swap";
    document.head.append(link);
  }, []);

  return (
    <div className="home-corp min-h-screen" style={{ background: PAPER, ["--brand-accent"]: LIME }} data-testid="home-corp">
      <style>{`
        .home-corp, .home-corp h1, .home-corp h2, .home-corp h3, .home-corp button, .home-corp input {
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, sans-serif;
          font-feature-settings: "cv11","ss01";
          -webkit-font-smoothing: antialiased;
        }
        /* dark, solid nav + solid ink mega-menu panels */
        .home-corp header { background: rgba(11,25,20,0.92) !important; border-bottom-color: rgba(255,255,255,0.08) !important; }
        .home-corp header .text-slate-700, .home-corp header .text-slate-500 { color: rgba(255,255,255,0.62) !important; }
        .home-corp header .text-slate-900 { color: #ffffff !important; }
        .home-corp header .bg-white { background: ${INK} !important; }
        .home-corp header .border-slate-200, .home-corp header .border-slate-100 { border-color: rgba(255,255,255,0.10) !important; }
        .home-corp header .bg-slate-50, .home-corp header .bg-slate-100 { background: rgba(255,255,255,0.05) !important; }
        .home-corp header .bg-slate-50\\/70 { background: rgba(255,255,255,0.04) !important; }
        .home-corp header .bg-\\[\\#0c1612\\] { background: ${LIME} !important; }
        .home-corp header .bg-\\[\\#0c1612\\] span, .home-corp header .bg-\\[\\#0c1612\\] svg { color: ${INK} !important; }
        .home-corp .partner-logo { filter: grayscale(1); opacity: 0.5; transition: opacity .2s ease, filter .2s ease; }
        .home-corp .partner-logo:hover { filter: none; opacity: 1; }
      `}</style>

      <PreviewNavHeader modeOverride="seller" />

      {/* ═══ HERO — ink, left-aligned, product UI right ═══ */}
      <section className="relative overflow-hidden" style={{ background: INK }} data-testid="hc-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <div className="lg:col-span-5">
              <Rise>
                <Label dark>Wholesale banking</Label>
                <h1 className="mt-5 text-[40px] md:text-[52px] lg:text-[54px] font-semibold tracking-[-0.025em] leading-[1.06] text-white">
                  The operating system for wholesale trade.
                </h1>
              </Rise>
              <Rise delay={0.08}>
                <p className="mt-6 text-[17px] leading-relaxed text-white/60 max-w-[46ch]">
                  Sell on terms, get paid the same day, and ship — with orders, payments,
                  buyer credit, freight and trade insurance settled into one account.
                </p>
              </Rise>
              <Rise delay={0.14}>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <a href="/waitlist?as=seller" data-testid="hc-cta-primary"
                     className="inline-flex items-center gap-2 h-11 px-6 rounded-lg text-[14px] font-semibold transition-colors"
                     style={{ background: LIME, color: INK }}>
                    Get started <ArrowRight className="w-4 h-4" />
                  </a>
                  <Link to="/pricing" data-testid="hc-cta-secondary"
                        className="inline-flex items-center gap-2 h-11 px-6 rounded-lg text-[14px] font-medium text-white border border-white/20 hover:bg-white/[0.06] transition-colors">
                    Talk to sales <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="mt-5 text-[12.5px] text-white/35">
                  No platform fees to start · first funded invoice in 48 hours
                </div>
              </Rise>
            </div>

            <div className="lg:col-span-7 relative">
              <Rise delay={0.12}>
                <div className="relative">
                  <UIFrame dark src="/preview-screens/seller/dashboard-hero.jpeg" url="app.verqo.com/seller" alt="Verqo seller account" />
                  <Callout label="Invoice funded" value="$48,200" meta="day 1" className="-left-16 top-24" delay={0.4} />
                  <Callout label="Buyer trade score" value="94" meta="Net-60 approved" className="-right-14 top-10" delay={0.5} />
                  <Callout label="Reconciled" value="PO #4821" meta="auto" className="-right-16 bottom-16" delay={0.6} />
                </div>
              </Rise>
            </div>
          </div>

          {/* metrics strip — product truth, inside the ink band */}
          <Rise delay={0.2}>
            <div className="mt-16 lg:mt-20 border-t border-white/10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10" data-testid="hc-metrics">
              {[
                ["60 sec", "buyer credit decision"],
                ["Day 1", "payout on approved invoices"],
                ["100%", "of funded invoices insured"],
                ["75+", "carriers, parcel to truckload"],
              ].map(([v, l], i) => (
                <div key={l} className={`py-7 ${i % 2 === 0 ? "pr-6" : "px-6"} lg:px-8 first:pl-0`}>
                  <div className="text-[30px] font-semibold tracking-[-0.02em] text-white">{v}</div>
                  <div className="mt-1.5 text-[12.5px] text-white/45">{l}</div>
                </div>
              ))}
            </div>
          </Rise>
        </div>
      </section>

      {/* ═══ PARTNER WALL — cream, monochrome ═══ */}
      <section className="border-b" style={{ borderColor: "rgba(11,25,20,0.08)", background: "#fff" }} data-testid="hc-partners">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row lg:items-center gap-8">
            <Label className="lg:w-56 flex-shrink-0">Connected to your stack</Label>
            <div className="flex-1 grid grid-cols-4 lg:grid-cols-8 gap-x-8 gap-y-6 items-center">
              {PARTNERS.map((p) => (
                <img key={p.d} src={`https://icons.duckduckgo.com/ip3/${p.d}.ico`} alt={p.n} title={p.n}
                     className="partner-logo h-7 w-auto object-contain justify-self-center"
                     onError={(e) => { e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${p.d}&sz=128`; }} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ BENTO — white cards on cream ═══ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28" data-testid="hc-bento">
        <Rise>
          <div className="max-w-2xl">
            <Label>One account</Label>
            <h2 className="mt-4 text-[30px] md:text-[38px] font-semibold tracking-[-0.02em] leading-[1.15] text-slate-900">
              Every part of a wholesale order, settled in one place.
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-slate-600">
              Orders, payouts, credit, freight and insurance share the same ledger — so
              nothing is re-keyed, exported, or matched by hand.
            </p>
          </div>
        </Rise>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          {[
            { icon: Banknote, t: "Paid on Day 1", b: "Approved invoices are funded the same day. Net-30/60/90 becomes your buyer's timeline, not your cash-flow problem.", img: "/preview-screens/seller/invoices.png", to: "/product/payments", span: "md:col-span-7" },
            { icon: Wallet, t: "One balance", b: "Payouts, postage, fees and credit in a single account and statement.", img: "/preview-screens/seller/wallet.png", to: "/product/payments", span: "md:col-span-5" },
            { icon: Radar, t: "Buyer credit, underwritten live", b: "Trade scores from real payment behaviour across the network — terms in 60 seconds.", img: "/preview-screens/seller/credit.png", to: "/product/credit-terms", span: "md:col-span-5" },
            { icon: ShieldCheck, t: "Insured end to end", b: "Every funded invoice is underwritten by Allianz Trade. Claims are filed and settled in-platform, so a default never reaches your P&L.", img: "/preview-screens/seller/insurance.png", to: "/product/insurance", span: "md:col-span-7" },
          ].map((c, i) => (
            <Rise key={c.t} delay={i * 0.05} className={c.span}>
              <Link to={c.to} data-testid={`hc-bento-${c.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group h-full flex flex-col rounded-2xl bg-white overflow-hidden transition-shadow"
                    style={{ border: "1px solid rgba(11,25,20,0.08)", boxShadow: "0 1px 2px rgba(11,25,20,0.03)" }}>
                <div className="p-7 pb-0">
                  <div className="flex items-center gap-2.5">
                    <c.icon className="w-[18px] h-[18px] text-slate-900" strokeWidth={1.7} />
                    <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-slate-900">{c.t}</h3>
                  </div>
                  <p className="mt-3 text-[14px] leading-relaxed text-slate-600 max-w-[52ch]">{c.b}</p>
                </div>
                <div className="mt-7 px-7 flex-1">
                  <div className="h-[220px] md:h-[260px] rounded-t-lg overflow-hidden" style={{ border: "1px solid rgba(11,25,20,0.07)", borderBottom: "none" }}>
                    <img src={c.img} alt={c.t} loading="lazy" className="block w-full h-full object-cover object-top" />
                  </div>
                </div>
              </Link>
            </Rise>
          ))}
        </div>
      </section>

      {/* ═══ DEEP DIVES ═══ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20 md:pb-28 space-y-20 md:space-y-28" data-testid="hc-deepdives">
        {[
          {
            icon: Store, label: "Sell", t: "A wholesale storefront your buyers can actually order from.",
            b: "Publish buyer-specific pricing, import your catalog from Shopify or CSV, and answer RFQs without a spreadsheet.",
            bullets: ["Buyer-tiered price lists and MOQs", "Catalog import in minutes", "RFQ inbox with one-click quoting"],
            img: "/preview-screens/portal-catalog/catalog-grid.jpeg", url: "app.verqo.com/seller/catalog", to: "/product/catalog",
          },
          {
            icon: Truck, label: "Ship", t: "Parcel, pallet and truckload — booked from the same balance.",
            b: "Rate-shop 75+ carriers, buy labels and freight from your wallet, and let tracking post back to the order automatically.",
            bullets: ["Parcel and LTL/FTL in one flow", "Landed cost on every order", "Tracking reconciled to the invoice"],
            img: "/preview-screens/buyer-shipping/main.jpeg", url: "app.verqo.com/seller/shipping", to: "/product/shipping",
          },
        ].map((d, i) => (
          <div key={d.t} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${i === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <Rise className="lg:col-span-5">
              <div>
                <div className="flex items-center gap-2">
                  <d.icon className="w-4 h-4 text-slate-900" strokeWidth={1.8} />
                  <Label>{d.label}</Label>
                </div>
                <h3 className="mt-4 text-[27px] md:text-[34px] font-semibold tracking-[-0.02em] leading-[1.15] text-slate-900">{d.t}</h3>
                <p className="mt-4 text-[15.5px] leading-relaxed text-slate-600 max-w-[48ch]">{d.b}</p>
                <ul className="mt-6 space-y-3 border-t pt-6" style={{ borderColor: "rgba(11,25,20,0.08)" }}>
                  {d.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[14px] text-slate-700">
                      <span className="mt-[7px] w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: INK }} />
                      {b}
                    </li>
                  ))}
                </ul>
                <Link to={d.to} className="mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-slate-900 hover:opacity-70 transition-opacity">
                  Learn more <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </Rise>
            <Rise delay={0.08} className="lg:col-span-7">
              <UIFrame src={d.img} url={d.url} alt={d.t} />
            </Rise>
          </div>
        ))}
      </section>

      {/* ═══ BANKING BAND — ink ═══ */}
      <section className="relative" style={{ background: INK }} data-testid="hc-banking">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <Rise>
            <div className="max-w-2xl">
              <Label dark>Banking at the core</Label>
              <h2 className="mt-4 text-[30px] md:text-[40px] font-semibold tracking-[-0.022em] leading-[1.12] text-white">
                Money arrives already matched to the order.
              </h2>
              <p className="mt-4 text-[16px] leading-relaxed text-white/55">
                Everywhere else the bank sits outside the system and your team reconciles it by hand.
                On verqo the account is part of the ledger.
              </p>
            </div>
          </Rise>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Rise>
              <div className="h-full rounded-2xl p-7" style={{ border: "1px solid rgba(255,255,255,0.10)" }}>
                <Label dark>Without verqo</Label>
                <div className="mt-6 space-y-2.5">
                  {FLOW.map((f) => (
                    <div key={f.t} className="flex items-center justify-between py-2.5 border-b last:border-0" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                      <span className="flex items-center gap-2.5 text-[14px] text-white/60"><f.i className="w-4 h-4" strokeWidth={1.7} />{f.t}</span>
                      <span className="text-[12px] font-mono text-white/25">unmatched</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.10)" }}>
                  <span className="text-[14px] text-white/50">Bank statement</span>
                  <span className="text-[12.5px] text-white/40">reconciled by hand · weeks late</span>
                </div>
              </div>
            </Rise>
            <Rise delay={0.08}>
              <div className="h-full rounded-2xl p-7" style={{ border: `1px solid rgba(220,255,58,0.30)`, background: "rgba(220,255,58,0.05)" }}>
                <Label dark className="!text-white/60">With verqo</Label>
                <div className="mt-6 space-y-2.5">
                  {FLOW.map((f) => (
                    <div key={f.t} className="flex items-center justify-between py-2.5 border-b last:border-0" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                      <span className="flex items-center gap-2.5 text-[14px] text-white"><f.i className="w-4 h-4" strokeWidth={1.7} />{f.t}</span>
                      <span className="text-[12px] font-mono" style={{ color: LIME }}>matched</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.10)" }}>
                  <span className="flex items-center gap-2.5 text-[14px] text-white"><Building2 className="w-4 h-4" strokeWidth={1.7} />verqo account</span>
                  <span className="text-[12.5px]" style={{ color: LIME }}>reconciled on settlement</span>
                </div>
              </div>
            </Rise>
          </div>
        </div>
      </section>

      {/* ═══ COMPLIANCE BAR ═══ */}
      <section className="border-b" style={{ background: "#fff", borderColor: "rgba(11,25,20,0.08)" }} data-testid="hc-compliance">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            [Landmark, "Partner banks, Members FDIC", "Deposits held at insured institutions"],
            [ShieldCheck, "Allianz Trade underwriting", "Credit insurance on funded invoices"],
            [Lock, "Encryption in transit & at rest", "TLS 1.3 · AES-256"],
            [BadgeCheck, "SOC 2 Type II — in progress", "Independent audit underway"],
          ].map(([Icon, t, s]) => (
            <div key={t} className="flex items-start gap-3">
              <Icon className="w-[18px] h-[18px] text-slate-900 mt-0.5 flex-shrink-0" strokeWidth={1.7} />
              <div>
                <div className="text-[13px] font-semibold text-slate-900 leading-snug">{t}</div>
                <div className="mt-0.5 text-[12px] text-slate-500 leading-snug">{s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ HOW IT WORKS + TESTIMONIAL ═══ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28" data-testid="hc-how">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Rise className="lg:col-span-4">
            <div>
              <Label>Implementation</Label>
              <h2 className="mt-4 text-[28px] md:text-[34px] font-semibold tracking-[-0.02em] leading-[1.15] text-slate-900">
                Live in 48 hours, not two quarters.
              </h2>
              <p className="mt-4 text-[15.5px] leading-relaxed text-slate-600">
                No implementation project, no integration fees. Import your catalog and buyers,
                and start invoicing on terms the same week.
              </p>
            </div>
          </Rise>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              [Layers, "01", "Connect", "Import catalog and buyers. Every buyer is credentialed against the network."],
              [FileText, "02", "Sell on terms", "Buyers check out on Net-30/60/90, approved in under a minute."],
              [Banknote, "03", "Get paid", "The invoice is funded the same day, insured, and reconciled on settlement."],
            ].map(([Icon, n, t, b], i) => (
              <Rise key={t} delay={i * 0.05}>
                <div className="h-full rounded-2xl bg-white p-7" style={{ border: "1px solid rgba(11,25,20,0.08)" }}>
                  <div className="flex items-center justify-between">
                    <Icon className="w-[18px] h-[18px] text-slate-900" strokeWidth={1.7} />
                    <span className="text-[11px] font-mono text-slate-400">{n}</span>
                  </div>
                  <div className="mt-5 text-[16px] font-semibold text-slate-900">{t}</div>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">{b}</p>
                </div>
              </Rise>
            ))}
          </div>
        </div>

        <Rise delay={0.1}>
          <figure className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start border-t pt-14" style={{ borderColor: "rgba(11,25,20,0.08)" }}>
            <blockquote className="lg:col-span-8 text-[22px] md:text-[26px] font-medium tracking-[-0.015em] leading-[1.35] text-slate-900">
              We replaced five systems and sixty days of waiting with one account. Our books
              reconcile themselves now, and we stopped chasing buyers for money we&apos;d already earned.
            </blockquote>
            <figcaption className="lg:col-span-4 lg:pt-2">
              <div className="text-[14px] font-semibold text-slate-900">Director of Operations</div>
              <div className="mt-1 text-[13px] text-slate-500">Mid-market beverage distributor · $40M revenue</div>
              <div className="mt-5 grid grid-cols-2 gap-4 border-t pt-5" style={{ borderColor: "rgba(11,25,20,0.08)" }}>
                <div><div className="text-[22px] font-semibold tracking-tight text-slate-900">62 → 0</div><div className="text-[12px] text-slate-500 mt-1">days to cash</div></div>
                <div><div className="text-[22px] font-semibold tracking-tight text-slate-900">5 → 1</div><div className="text-[12px] text-slate-500 mt-1">systems in the stack</div></div>
              </div>
            </figcaption>
          </figure>
        </Rise>
      </section>

      {/* ═══ FINAL CTA — ink ═══ */}
      <section style={{ background: INK }} data-testid="hc-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <h2 className="text-[32px] md:text-[44px] font-semibold tracking-[-0.025em] leading-[1.08] text-white">
                Run wholesale on one system.
              </h2>
              <p className="mt-4 text-[16px] text-white/55 max-w-[52ch]">
                Sell on terms, get paid the same day, ship and insure from one account.
                First funded invoice in 48 hours.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap lg:justify-end gap-3">
              <a href="/waitlist?as=seller" data-testid="hc-cta-footer-primary"
                 className="inline-flex items-center gap-2 h-12 px-7 rounded-lg text-[14px] font-semibold transition-colors"
                 style={{ background: LIME, color: INK }}>
                Get started <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/pricing" className="inline-flex items-center gap-2 h-12 px-7 rounded-lg text-[14px] font-medium text-white border border-white/20 hover:bg-white/[0.06] transition-colors">
                Talk to sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ DENSE FINTECH FOOTER ═══ */}
      <footer style={{ background: INK }} data-testid="hc-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-10">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
            {[
              ["Product", [["Catalog & storefront", "/product/catalog"], ["Payments & payouts", "/product/payments"], ["Buyer credit terms", "/product/credit-terms"], ["Seller financing", "/product/financing"], ["Shipping & freight", "/product/shipping"], ["Trade insurance", "/product/insurance"]]],
              ["Solutions", [["For sellers", "/"], ["For buyers", "/buyers"], ["Distributors", "/solutions/distributors"], ["Brands", "/solutions/brands"], ["Dropship", "/dropship"], ["Pricing", "/pricing"]]],
              ["Company", [["About", "/about"], ["Story", "/company/story"], ["Partners", "/partners"], ["Investors", "/pitch"], ["Contact", "/contact"]]],
              ["Resources", [["Banking docs", "/banking-docs"], ["Architecture", "/architecture"], ["Security", "/legal/security"], ["Status", "/status"]]],
              ["Legal", [["Terms of service", "/legal/terms"], ["Privacy policy", "/legal/privacy"], ["Financing disclosures", "/legal/financing"], ["Insurance terms", "/legal/insurance"]]],
            ].map(([h, links]) => (
              <div key={h}>
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-white/35">{h}</div>
                <ul className="mt-5 space-y-3">
                  {links.map(([l, to]) => (
                    <li key={l}>
                      <Link to={to} className="text-[13px] text-white/60 hover:text-white transition-colors">{l}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-white/35">Get started</div>
              <a href="/waitlist?as=seller" className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-lg text-[13px] font-semibold" style={{ background: LIME, color: INK }}>
                Create account <ArrowRight className="w-3.5 h-3.5" />
              </a>
              <div className="mt-5 text-[12.5px] text-white/40 leading-relaxed">sales@verqo.com<br />Mon–Fri · 8am–6pm ET</div>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <p className="text-[11.5px] leading-relaxed text-white/30 max-w-5xl">
              Verqo is a financial technology company, not a bank. Banking services are provided by our
              partner banks, Members FDIC. Deposit insurance covers the failure of an insured depository
              institution. Credit and financing products are subject to underwriting, approval and
              applicable state licensing; terms and pricing vary by counterparty and are disclosed before
              acceptance. Trade credit insurance is underwritten by third-party insurers and is subject to
              policy terms, limits and exclusions. Shipping rates are provided by third-party carriers.
              Nothing on this page is an offer of credit or insurance.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <div className="text-[11.5px] text-white/30">© {new Date().getFullYear()} Verqo, Inc. All rights reserved.</div>
              <div className="flex items-center gap-6 text-[11.5px] text-white/40">
                <Link to="/legal/terms" className="hover:text-white transition-colors">Terms</Link>
                <Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link to="/legal/security" className="hover:text-white transition-colors">Security</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
